/**
 * React Query hooks for asset type management
 */

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { assetTypesApi } from '../services/api/assetTypes';
import type { AssetTypeCreateData, AssetTypeUpdateData } from '../types/asset';
import { useEffectiveCompanyId } from './useEffectiveCompany';
import { useToast } from './useToast';

/**
 * Hook to list asset types
 */
export const useAssetTypes = (activeOnly: boolean = true) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['assetTypes', companyId, activeOnly],
    () => assetTypesApi.list(companyId, activeOnly),
    {
      enabled: !!companyId,
    }
  );
};

/**
 * Hook to get a single asset type
 */
export const useAssetType = (assetTypeId: string) => {
  const companyId = useEffectiveCompanyId();

  return useQuery(
    ['assetTypes', assetTypeId, companyId],
    () => assetTypesApi.getById(assetTypeId, companyId),
    {
      enabled: !!assetTypeId && !!companyId,
    }
  );
};

/**
 * Hook to create an asset type
 */
export const useCreateAssetType = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (data: AssetTypeCreateData) => assetTypesApi.create(companyId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetTypes', companyId]);
        showSuccess('Tipo de activo creado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al crear tipo de activo');
      },
    }
  );
};

/**
 * Hook to update an asset type
 */
export const useUpdateAssetType = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    ({ assetTypeId, data }: { assetTypeId: string; data: AssetTypeUpdateData }) =>
      assetTypesApi.update(assetTypeId, companyId, data),
    {
      onSuccess: (updatedAssetType) => {
        queryClient.invalidateQueries(['assetTypes', companyId]);
        queryClient.setQueryData(
          ['assetTypes', updatedAssetType.assetTypeId, companyId],
          updatedAssetType
        );
        showSuccess('Tipo de activo actualizado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al actualizar tipo de activo');
      },
    }
  );
};

/**
 * Hook to delete an asset type
 */
export const useDeleteAssetType = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (assetTypeId: string) => assetTypesApi.delete(assetTypeId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetTypes', companyId]);
        showSuccess('Tipo de activo eliminado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al eliminar tipo de activo');
      },
    }
  );
};

/**
 * Hook to deactivate an asset type
 */
export const useDeactivateAssetType = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (assetTypeId: string) => assetTypesApi.deactivate(assetTypeId, companyId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetTypes', companyId]);
        showSuccess('Tipo de activo desactivado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al desactivar tipo de activo');
      },
    }
  );
};

/**
 * Hook to activate an asset type
 */
export const useActivateAssetType = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const companyId = useEffectiveCompanyId();

  return useMutation(
    (assetTypeId: string) => assetTypesApi.update(assetTypeId, companyId, { isActive: true }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assetTypes', companyId]);
        showSuccess('Tipo de activo activado exitosamente');
      },
      onError: (error: any) => {
        showError(error, 'Error al activar tipo de activo');
      },
    }
  );
};
